import { resolveChannelDisplay } from '../models/channelDisplay'
import {
  GET_STARTED_PLATFORM_STAGES,
  type GetStartedPlatformProgressDto,
  type GetStartedPlatformProgressItem,
  type GetStartedPlatformState,
  type GetStartedProgressContext,
  type GetStartedProgressPayloadDto,
  type GetStartedProgressRetryCommand,
} from '../models/getStartedProgress.types'
import { providersById, resolveProvider } from '../models/providers'

const READY = new Set(['done', 'ready', 'success', 'completed'])
const RUNNING = new Set(['running', 'in_progress', 'in-progress', 'processing', 'active'])
const WAITING = new Set(['waiting', 'queued', 'pending', 'not_started', 'not-started'])
const ERROR = new Set(['error', 'failed', 'stale'])

const PROVIDER_ALIASES: Readonly<Record<string, string>> = {
  ga4_analytics_account: 'ga4_analytics_property',
  google_analytics_account: 'ga4_analytics_property',
  google_merchant_center_account: 'merchant_center_account',
  google_search_console_account: 'search_console',
  app_store_account: 'app_store',
  meta_pixel_account: 'meta_pixel',
}

const STAGE_ALIASES: Readonly<Record<string, number>> = {
  connected: 0,
  connect: 0,
  fetching: 1,
  fetching_data: 1,
  raw_data: 1,
  enriching: 2,
  enrichment: 2,
  analysing: 3,
  analyzing: 3,
  analysis: 3,
  ready: 4,
  done: 4,
  completed: 4,
}

function text(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
}

function humanizeProvider(value: string): string {
  const source = value || 'Unknown platform'
  return source
    .replace(/_(account|property|store|pixel|channel)$/, '')
    .split('_')
    .filter(Boolean)
    .map((part) => {
      const normalized = part.toLowerCase()
      if (normalized === 'ga4') return 'GA4'
      if (normalized === 'bigquery') return 'BigQuery'
      return `${part.charAt(0).toUpperCase()}${part.slice(1)}`
    })
    .join(' ')
}

function normalizedState(value: unknown): GetStartedPlatformState {
  const status = text(value).toLowerCase()
  if (READY.has(status)) return 'ready'
  if (ERROR.has(status)) return 'error'
  if (WAITING.has(status)) return 'waiting'
  if (RUNNING.has(status)) return 'running'
  return 'running'
}

function activeStage(row: GetStartedPlatformProgressDto, state: GetStartedPlatformState): number {
  if (state === 'ready') return GET_STARTED_PLATFORM_STAGES.length - 1
  const raw = row.current_stage ?? row.progress_stage ?? row.stage
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.max(0, Math.min(GET_STARTED_PLATFORM_STAGES.length - 1, Math.trunc(raw)))
  }
  const normalized = text(raw)
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  return STAGE_ALIASES[normalized] ?? (state === 'running' ? 1 : 0)
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Rejects absolute/cross-context URLs and accepts only a retry command under this progress API. */
export function validateGetStartedRetryPath(
  value: unknown,
  context: GetStartedProgressContext,
): GetStartedProgressRetryCommand | null {
  const path = text(value)
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('?') || path.includes('#')) {
    return null
  }
  const base = `/accounts/${encodeURIComponent(context.accountId)}/workspaces/${encodeURIComponent(context.workspaceId)}/onboarding/progress/`
  const pattern = new RegExp(`^${escapeRegExp(base)}(?:[A-Za-z0-9_-]+/)?retry/$`)
  return pattern.test(path) ? { path } : null
}

function rowsFrom(payload: GetStartedProgressPayloadDto | readonly unknown[]): unknown[] {
  if (Array.isArray(payload)) return [...payload]
  const root = payload as GetStartedProgressPayloadDto
  if (Array.isArray(root.platforms)) return root.platforms
  if (root.data && typeof root.data === 'object') {
    const nested = root.data as GetStartedProgressPayloadDto
    if (Array.isArray(nested.platforms)) return nested.platforms
  }
  return []
}

export function normalizeGetStartedProgress(
  payload: GetStartedProgressPayloadDto | readonly unknown[],
  context: GetStartedProgressContext,
): GetStartedPlatformProgressItem[] {
  return rowsFrom(payload).flatMap((value): GetStartedPlatformProgressItem[] => {
    if (!value || typeof value !== 'object') return []
    const row = value as GetStartedPlatformProgressDto
    const integrationType = text(row.integration_type)
    const id = text(row.id ?? row.integration_id) || integrationType
    if (!id) return []

    const registryKey = PROVIDER_ALIASES[integrationType] ?? integrationType
    const provider = resolveProvider(registryKey) ?? providersById(registryKey)[0] ?? null
    const channel = resolveChannelDisplay(registryKey)
    const state = normalizedState(row.status ?? row.state)
    const backendName = text(row.provider_name ?? row.platform_name ?? row.name ?? row.label)

    return [
      {
        id,
        integrationType,
        nameKey: provider?.nameKey ?? channel?.nameKey ?? null,
        name: backendName || humanizeProvider(integrationType),
        logoKey: provider?.logo ?? channel?.logo ?? null,
        state,
        activeStage: activeStage(row, state),
        stages: state === 'error' ? [] : GET_STARTED_PLATFORM_STAGES,
        errorMessage: text(row.error_message ?? row.error),
        retryCommand:
          state === 'error'
            ? validateGetStartedRetryPath(row.retry_url ?? row.retryUrl, context)
            : null,
      },
    ]
  })
}

export function isGetStartedProgressEvent(payload: unknown, workspaceId: number | string): boolean {
  if (!payload || typeof payload !== 'object') return false
  const row = payload as Record<string, unknown>
  if (text(row.workspace_id) !== String(workspaceId)) return false
  const taskType = text(row.task_type).toUpperCase()
  return (
    ['RAW_DATA', 'ANALYSIS', 'ANALYSIS_RUN', 'STEP'].includes(taskType) ||
    Boolean(text(row.task_id ?? row.task_name))
  )
}
